import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import api from '../services/api'
import EmailOtpModal from './EmailOtpModal'
import { getToken, saveToken, saveUserData, clearAuth } from '../utils/userData'
import { useLoginModal } from '../contexts/LoginModalContext'
import { ROUTES } from '../utils/constants'
import { identifyUser, trackEvent, resetMixpanel } from '../utils/mixpanel'

// Program display config
const PROGRAM_CONFIG = {
  'build3-cohort-7': {
    name: 'Build3 Energizer Sessions',
    emoji: '🧘',
    dashboardPath: ROUTES.COHORT_DASHBOARD('build3-cohort-7'),
    // Uses login modal flow from navbar
  },
  'energizers-march-cohort': {
    name: 'Energizers - March Cohort',
    emoji: '🔥',
    dashboardPath: ROUTES.COHORT_DASHBOARD('energizers-march-cohort'),
    // Uses login modal flow from navbar
  },
  'energizers-june-cohort': {
    name: 'Energizers - June Cohort',
    emoji: '☀️',
    dashboardPath: ROUTES.COHORT_DASHBOARD('energizers-june-cohort'),
  },
  'mid-day-reset': {
    name: 'Mid-Day Reset',
    emoji: '🌅',
    dashboardPath: '/mid-day-reset/dashboard',
    landingPage: '/mid-day-reset', // Always navigate here from navbar
  },
}

const Navbar = () => {
  const navigate = useNavigate()
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [programsDropdownOpen, setProgramsDropdownOpen] = useState(false)
  const { loginModalOpen, loginContext, openLoginModal, closeLoginModal } = useLoginModal()
  const dropdownRef = useRef(null)
  const programsDropdownRef = useRef(null)

  useEffect(() => {
    // Check if user is logged in
    const token = getToken()
    if (token) {
      fetchCurrentUser()
    } else {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
      if (programsDropdownRef.current && !programsDropdownRef.current.contains(event.target)) {
        setProgramsDropdownOpen(false)
      }
    }

    if (dropdownOpen || programsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen, programsDropdownOpen])

  const fetchCurrentUser = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/auth/me')
      const userData = response.data.data
      setUser(userData)
      saveUserData(userData)
      identifyUser(userData.id, {
        '$name': userData.name,
        '$email': userData.email,
      })
      setIsLoggedIn(true)
    } catch (error) {
      console.error('Error fetching user:', error)
      // Token invalid - clear auth
      clearAuth()
      setIsLoggedIn(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.trim().split(' ')
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase()
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
  }

  const handleLogout = () => {
    resetMixpanel()
    clearAuth()
    setIsLoggedIn(false)
    setUser(null)
    setDropdownOpen(false)
    navigate('/')
  }

  const handleProgramClick = (programSlug) => {
    setProgramsDropdownOpen(false)
    const config = PROGRAM_CONFIG[programSlug]
    
    // If program has a landing page, always navigate there (e.g., mid-day-reset)
    if (config?.landingPage) {
      navigate(config.landingPage)
      return
    }
    
    // If logged in and enrolled in program, go to dashboard
    if (isLoggedIn && user?.enrolledPrograms?.some(p => p.programSlug === programSlug)) {
      if (config) {
        navigate(config.dashboardPath)
        return
      }
    }
    
    // Logged in but not enrolled - go directly to signup with email prefilled
    if (isLoggedIn && user) {
      navigate(ROUTES.COHORT_SIGNUP(programSlug), { 
        state: { 
          prefillEmail: user.email,
          ...(user.name && { prefillName: user.name }),
          ...(user.phone && { prefillPhone: user.phone }),
        } 
      })
      return
    }

    // Not logged in - open login modal with program context
    openLoginModal(programSlug)
  }

  const handleLoginClick = () => {
    openLoginModal('general')
  }

  const handleLoginSuccess = async (data) => {
    const { token, user: loggedInUser } = data
    saveToken(token)
    trackEvent('Sign In', {
      login_method: 'email',
      success: true,
    })
    
    // Fetch full user data from /api/auth/me
    await fetchCurrentUser()
    
    // Navigate based on context and user type
    if (loginContext === 'general') {
      // For general login, navigate based on whether user is a facilitator
      if (loggedInUser?.isFacilitator) {
        navigate('/facilitator/dashboard')
      } else {
        navigate('/dashboard')
      }
    } else if (loginContext && PROGRAM_CONFIG[loginContext]) {
      // For program-based login, go to program dashboard
      navigate(PROGRAM_CONFIG[loginContext].dashboardPath)
    }
    
    closeLoginModal()
  }

  const handleUserNotFound = (email) => {
    if (loginContext === 'build3-cohort-7') {
      navigate(ROUTES.COHORT_SIGNUP('build3-cohort-7'), { state: { prefillEmail: email } })
    } else if (loginContext === 'energizers-march-cohort') {
      navigate(ROUTES.COHORT_SIGNUP('energizers-march-cohort'), { state: { prefillEmail: email } })
    } else if (loginContext === 'energizers-june-cohort') {
      navigate(ROUTES.COHORT_SIGNUP('energizers-june-cohort'), { state: { prefillEmail: email } })
    } else if (loginContext === 'mid-day-reset') {
      navigate('/mid-day-reset', { state: { prefillEmail: email } })
    }
    closeLoginModal()
  }

  // Get modal config based on login context
  const getModalConfig = () => {
    if (loginContext === 'general') {
      // General login - for all users (participants and facilitators)
      return {
        title: 'Login',
        emoji: '👤',
        emailDescription: 'Enter your email to continue',
        emailHint: "If you're registered, we'll send a code. Otherwise, you can sign up!",
        allowNewUserRegistration: true,
        registerDescription: 'Complete your profile to continue',
        requestOtp: async (email) => {
          const response = await api.post('/api/auth/otp', { 
            email, 
            createIfNotExists: true 
          })
          return {
            success: response.data.success,
            userExists: response.data.data?.userExists,
            code: response.data.data?.code,
          }
        },
        verifyOtp: async (email, code) => {
          const response = await api.post('/api/auth/verify-otp', { email, code })
          return response.data
        },
        registerNewUser: async (email, name, phone) => {
          const response = await api.post('/api/auth/complete-registration', { 
            email, 
            name, 
            phone 
          })
          return response.data
        },
        showUserNotFound: false,
      }
    }
    
    // Program-based login
    const programConfig = PROGRAM_CONFIG[loginContext] || {}
    return {
      title: programConfig.name || 'Login',
      emoji: programConfig.emoji || '🔐',
      emailDescription: 'Enter your email to continue',
      emailHint: "If you're already signed up, we'll send you a verification code. If not, we'll help you get started!",
      requestOtp: async (email) => {
        const response = await api.post('/api/auth/otp', {
          email,
          programSlug: loginContext,
        })
        return {
          success: response.data.success,
          userExists: response.data.data?.userExists,
          code: response.data.data?.code,
        }
      },
      verifyOtp: async (email, code) => {
        const response = await api.post('/api/auth/verify-otp', {
          email,
          code,
          programSlug: loginContext,
        })
        return response.data
      },
      showUserNotFound: true,
    }
  }

  const modalConfig = loginContext ? getModalConfig() : {}

  // Check if user is enrolled in any program
  const enrolledPrograms = user?.enrolledPrograms || []
  const isFacilitator = user?.isFacilitator || false

  const navItemClass =
    'text-gray-700 hover:text-primary-600 transition-colors inline-flex items-center justify-center min-h-10 text-sm md:text-base whitespace-nowrap rounded-md px-2'

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white shadow-md"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between gap-3 h-16">
          <Link to="/" className="flex items-center shrink-0 py-1">
            <img 
              src="/logo.png" 
              alt="Wellness Loop Logo" 
              className="h-10 sm:h-11 md:h-12 w-auto"
            />
          </Link>

          <div className="flex items-center justify-end gap-2 sm:gap-4 md:gap-6 flex-nowrap min-w-0">
            {/* Home Link */}
            {/* <Link
              to="/"
              className="text-gray-700 hover:text-primary-600 transition-colors hidden md:block"
            >
              Home
            </Link> */}
            <Link to="/events" className={navItemClass}>
              Events
            </Link>
            
            {/* Programs Dropdown */}
            <div className="relative shrink-0" ref={programsDropdownRef}>
              <button
                type="button"
                onClick={() => setProgramsDropdownOpen(!programsDropdownOpen)}
                className={`${navItemClass} gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2`}
                aria-label="Programs menu"
                aria-expanded={programsDropdownOpen}
              >
                <span>Programs</span>
                <motion.svg
                  className="w-4 h-4 shrink-0 text-current"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ rotate: programsDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </motion.svg>
              </button>

              {/* Programs Dropdown Menu */}
              <AnimatePresence>
                {programsDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  >
                    {/* <button
                      onClick={() => handleProgramClick('build3-cohort-7')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <span>🧘</span>
                      <span>Build3 Energizer Sessions</span>
                    </button> */}
                    <button
                      onClick={() => handleProgramClick('energizers-march-cohort')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <span>🔥</span>
                      <span>Energizers - March Cohort</span>
                    </button>
                    <button
                      onClick={() => handleProgramClick('energizers-june-cohort')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <span>☀️</span>
                      <span>Energizers - June Cohort</span>
                    </button>
                    <button
                      onClick={() => handleProgramClick('mid-day-reset')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                    >
                      <span>🌅</span>
                      <span>Mid-Day Reset</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Facilitator Onboarding - Only show if not logged in */}
            {!loading && !isLoggedIn && (
              <Link
                to="/for-facilitators"
                className={`${navItemClass} hidden md:inline-flex`}
              >
                Become a Facilitator
              </Link>
            )}

            {/* Login Button - Only show if not logged in */}
            {!loading && !isLoggedIn && (
              <button
                type="button"
                onClick={handleLoginClick}
                className={`${navItemClass} font-medium bg-transparent border-0 cursor-pointer shrink-0`}
              >
                Login
              </button>
            )}

            {/* Profile Icon - Only show if logged in */}
            {!loading && isLoggedIn && user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 min-h-10 pl-1 pr-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
                  aria-label="User menu"
                  aria-expanded={dropdownOpen}
                >
                  <div className="w-10 h-10 rounded-full bg-primary-600 text-white font-semibold flex items-center justify-center hover:bg-primary-700 transition-colors">
                    {getInitials(user.name)}
                  </div>
                  <motion.svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: dropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </motion.svg>
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                    >
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                          {user.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {user.email}
                        </p>
                        {isFacilitator && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                            Facilitator
                          </span>
                        )}
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {/* Dashboard link for all users */}
                        <Link
                          to="/dashboard"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          Dashboard
                        </Link>

                        {/* Facilitator-specific links */}
                        {isFacilitator && (
                          <>
                            <Link
                              to="/facilitator/dashboard"
                              onClick={() => setDropdownOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              Facilitator Dashboard
                            </Link>
                            <Link
                              to="/facilitators/edit-profile"
                              onClick={() => setDropdownOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              Edit Profile
                            </Link>
                          </>
                        )}

                        {/* Enrolled Programs */}
                        {enrolledPrograms.length > 0 && (
                          <>
                            {(isFacilitator) && (
                              <div className="border-t border-gray-100 my-1" />
                            )}
                            <div className="px-4 py-1">
                              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                My Programs
                              </p>
                            </div>
                            {enrolledPrograms.map((program) => {
                              const config = PROGRAM_CONFIG[program.programSlug]
                              if (!config) return null
                              return (
                                <Link
                                  key={program.programSlug}
                                  to={config.dashboardPath}
                                  onClick={() => setDropdownOpen(false)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <span>{config.emoji}</span>
                                  <span>{config.name}</span>
                                </Link>
                              )
                            })}
                          </>
                        )}

                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unified Login Modal */}
      {loginContext && (
        <EmailOtpModal
          isOpen={loginModalOpen}
          onClose={closeLoginModal}
          title={modalConfig.title}
          emoji={modalConfig.emoji}
          emailDescription={modalConfig.emailDescription}
          emailHint={modalConfig.emailHint}
          requestOtp={modalConfig.requestOtp}
          verifyOtp={modalConfig.verifyOtp}
          onSuccess={handleLoginSuccess}
          onUserNotFound={modalConfig.showUserNotFound ? handleUserNotFound : undefined}
          allowNewUserRegistration={modalConfig.allowNewUserRegistration}
          registerNewUser={modalConfig.registerNewUser}
          registerDescription={modalConfig.registerDescription}
        />
      )}
    </motion.nav>
  )
}

export default Navbar
